// recallStore.js — Datenschicht für PZR-Recalls.
// LIVE: Supabase (public.recalls, RLS) · DEMO: localStorage.
// Die Fälligkeit ("tage") wird IMMER beim Laden aus lastPzr + intervalTage
// berechnet — in der Datenbank liegt nur das Datum, nie der Zählerstand.
import { supabase } from "./supabase";
import { demoFallbacks, serverActive } from "./mode";
import { loadPatientsCache } from "./patientStore";
import { loadAptsCache } from "./aptStore";
import { getBehandler } from "./settingsStore";

const CACHE_KEY = "dida_recalls";

const daysSince = (d) => {
  try {
    if (!d) return null;
    const [dd, mm, yy] = d.split(".");
    const dt = new Date(2000 + +yy, +mm - 1, +dd);
    return Math.round((Date.now() - dt.getTime()) / 86400000);
  } catch { return null; }
};
export const recallDue = (r) => Math.max(0, (daysSince(r.lastPzr) ?? 0) - (r.intervalTage || 182));

// Demo-Seed (nur DEMO-Modus)
export const INITIAL_RECALLS = [
  { id:1,  name:"Wolf, Inge",      dob:"30.01.1952", lastPzr:"05.09.25", intervalTage:182, behandler:"Dr. Hoffmann", score:35, status:"offen" },
  { id:2,  name:"Braun, Max",      dob:"14.02.1978", lastPzr:"11.01.26", intervalTage:90,  behandler:"Dr. Schulz",   score:55, status:"offen" },
  { id:3,  name:"Schäfer, Tim",    dob:"22.08.1983", lastPzr:"02.11.25", intervalTage:182, behandler:"Dr. Hoffmann", score:42, status:"offen" },
  { id:4,  name:"Koch, Birgit",    dob:"17.04.1959", lastPzr:"19.11.25", intervalTage:182, behandler:"Maja Roth",    score:58, status:"offen" },
  { id:5,  name:"Müller, Ralf",    dob:"03.06.1971", lastPzr:"28.10.25", intervalTage:182, behandler:"Dr. Schulz",   score:61, status:"offen" },
  { id:6,  name:"Weber, Gerd",     dob:"11.09.1948", lastPzr:"14.11.25", intervalTage:182, behandler:"Dr. Hoffmann", score:66, status:"offen" },
  { id:7,  name:"Hoffstedt, Karin",dob:"25.12.1966", lastPzr:"05.12.25", intervalTage:182, behandler:"Maja Roth",    score:71, status:"kontaktiert" },
  { id:8,  name:"Lang, Peter",     dob:"08.03.1980", lastPzr:"02.12.25", intervalTage:182, behandler:"Dr. Bergmann", score:78, status:"gebucht" },
  { id:9,  name:"Vogt, Gregor",    dob:"02.11.1968", lastPzr:"22.11.25", intervalTage:140, behandler:"Dr. Schulz",   score:44, status:"offen" },
  { id:10, name:"Kraus, Nina",     dob:"19.06.2001", lastPzr:"10.12.25", intervalTage:182, behandler:"Maja Roth",    score:82, status:"gebucht" },
];

const withDue = (rows) => rows.map(r => ({ ...r, tage: recallDue(r) }));

const fromDb = (r) => {
  const { last_pzr, interval_tage, kanal_used, created_at, updated_at, ...rest } = r;
  return { ...rest, lastPzr: last_pzr, intervalTage: interval_tage, kanalUsed: kanal_used || undefined };
};
const toDb = (r) => {
  const out = {};
  if (r.name !== undefined) out.name = r.name;
  if (r.dob !== undefined) out.dob = r.dob;
  if (r.lastPzr !== undefined) out.last_pzr = r.lastPzr;
  if (r.intervalTage !== undefined) out.interval_tage = r.intervalTage;
  if (r.behandler !== undefined) out.behandler = r.behandler;
  if (r.score !== undefined) out.score = r.score;
  if (r.status !== undefined) out.status = r.status;
  if (r.kanalUsed !== undefined) out.kanal_used = r.kanalUsed;
  return out;
};

export const loadRecallsCache = () => {
  let rows;
  try { const s = localStorage.getItem(CACHE_KEY); if (s) rows = JSON.parse(s); } catch {}
  if (!rows) rows = demoFallbacks() ? INITIAL_RECALLS : [];
  return withDue(rows);
};
const saveCache = (rows) => { try { localStorage.setItem(CACHE_KEY, JSON.stringify(rows)); } catch {} };

export async function listRecalls() {
  if (!serverActive()) { const rows = loadRecallsCache(); saveCache(rows); return rows; }
  const { data, error } = await supabase.from("recalls").select("*").order("last_pzr");
  if (error) throw error;
  const rows = data.map(fromDb);
  saveCache(rows);
  return withDue(rows);
}

// Mehrere Recalls als kontaktiert markieren (Versand)
export async function markKontaktiert(ids, kanal) {
  if (!serverActive()) {
    const rows = loadRecallsCache().map(r => ids.includes(r.id) ? { ...r, status: "kontaktiert", kanalUsed: kanal } : r);
    saveCache(rows);
    return withDue(rows);
  }
  const { error } = await supabase.from("recalls").update({ status: "kontaktiert", kanal_used: kanal }).in("id", ids);
  if (error) throw error;
  return listRecalls();
}

// CSV-Import: Upsert über Name + Geburtsdatum
export async function importRecalls(rows, opts = {}) {
  const clean = rows.filter(r => (r.name || "").trim());
  const existing = await listRecalls();
  const key = (r) => `${(r.name || "").trim().toLowerCase()}|${(r.dob || "").trim()}`;
  const byKey = new Map(existing.map(r => [key(r), r]));
  const toAdd = [], toUpdate = [];
  clean.forEach(r => {
    const hit = byKey.get(key(r)) || existing.find(x => x.name === r.name && (!r.dob || !x.dob));
    if (hit) {
      if (r.addOnly) return; // Naeherungs-Basis (heute) darf echte Daten nie anfassen
      const patch = {};
      // onlyNewer (Patienten-Sync): lastPzr nur vorziehen, nie zurückdatieren —
      // und bei frischer PZR den Zyklus neu starten (Status zurück auf offen)
      const newer = (daysSince(r.lastPzr) ?? Infinity) < (daysSince(hit.lastPzr) ?? Infinity);
      if (r.lastPzr && r.lastPzr !== hit.lastPzr && (!opts.onlyNewer || newer)) {
        patch.lastPzr = r.lastPzr;
        if (opts.onlyNewer) patch.status = "offen";
      }
      if (r.behandler && r.behandler !== hit.behandler && !opts.onlyNewer) patch.behandler = r.behandler;
      if (Object.keys(patch).length) toUpdate.push({ id: hit.id, patch });
    } else {
      toAdd.push({ name: r.name, dob: r.dob || "", lastPzr: r.lastPzr || "", intervalTage: 182, behandler: r.behandler || "", score: r.score ?? 50, status: "offen" });
    }
  });

  if (!serverActive()) {
    let rows2 = loadRecallsCache();
    toAdd.forEach((r, i) => { rows2 = [...rows2, { ...r, id: Date.now() + i }]; });
    toUpdate.forEach(({ id, patch }) => { rows2 = rows2.map(x => x.id === id ? { ...x, ...patch } : x); });
    saveCache(rows2);
    return { added: toAdd.length, updated: toUpdate.length };
  }

  if (toAdd.length) {
    const { error } = await supabase.from("recalls").insert(toAdd.map(toDb));
    if (error) throw error;
  }
  for (const { id, patch } of toUpdate) {
    const { error } = await supabase.from("recalls").update(toDb(patch)).eq("id", id);
    if (error) throw error;
  }
  await listRecalls(); // Cache aktualisieren
  return { added: toAdd.length, updated: toUpdate.length };
}

// Status einzeln setzen (z.B. kontaktiert -> zurueck auf offen)
export async function setRecallStatus(id, status) {
  if (!serverActive()) {
    const rows = loadRecallsCache().map(r => r.id === id ? { ...r, status, ...(status === "offen" ? { kanalUsed: undefined } : {}) } : r);
    saveCache(rows);
    return withDue(rows);
  }
  const { error } = await supabase.from("recalls").update({ status, ...(status === "offen" ? { kanal_used: null } : {}) }).eq("id", id);
  if (error) throw error;
  return listRecalls();
}

// Einzelnen Recall manuell anlegen
export async function addRecall(r) {
  const row = { name: r.name, dob: r.dob || "", lastPzr: r.lastPzr || "", intervalTage: r.intervalTage || 182, behandler: r.behandler || "", score: 50, status: "offen" };
  if (!serverActive()) {
    const rows = [...loadRecallsCache(), { ...row, id: Date.now() }];
    saveCache(rows);
    return withDue(rows);
  }
  const { error } = await supabase.from("recalls").insert(toDb(row));
  if (error) throw error;
  return listRecalls();
}

// Recall-Liste aus dem Patientenstamm aufbauen/aktualisieren:
//  - Basis-Datum: letzter vergangener PZR-/Prophylaxe-Termin aus dem
//    Terminplan, sonst "Letzter Termin" des Patienten (Näherung)
//  - Upsert über Name+Geburtsdatum; bestehende Einträge werden nur
//    VORDATIERT (onlyNewer) — importierte PZR-Daten bleiben führend
export async function syncRecallsFromPatients() {
  const pats = loadPatientsCache().filter(p => p.status !== "Inaktiv");
  if (!pats.length) return { added: 0, updated: 0 };
  const behById = new Map(getBehandler().map(b => [b.id, b.name]));
  const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();
  const todayIso = new Date().toISOString().slice(0, 10);
  const pzrByPatient = new Map();
  loadAptsCache()
    .filter(a => a.date && a.date <= todayIso && a.status !== "abgesagt" && /pzr|prophylaxe/i.test(a.type || ""))
    .forEach(a => {
      const k = norm(a.patient);
      const cur = pzrByPatient.get(k);
      if (!cur || a.date > cur.date) pzrByPatient.set(k, a);
    });
  const isoToDe = (iso) => { const [y, m, d] = iso.split("-"); return `${d}.${m}.${y.slice(2)}`; };
  const heuteDe = isoToDe(todayIso);
  const rows = [];
  pats.forEach(p => {
    const name = `${p.nachname}, ${p.vorname}`;
    const hitApt = pzrByPatient.get(norm(name)) || pzrByPatient.get(norm(`${p.vorname} ${p.nachname}`));
    const echtesDatum = hitApt ? isoToDe(hitApt.date) : (p.last && p.last !== "—" ? p.last : "");
    // Neuaufnahme ohne jedes Datum: mit heute als Basis in den Recall
    // (erster Prophylaxe-Termin wird nach Intervall fällig) — aber nur
    // als Neuanlage, bestehende Einträge bleiben unberührt (addOnly).
    rows.push({ name, dob: p.dob || "", lastPzr: echtesDatum || heuteDe, addOnly: !echtesDatum,
      behandler: hitApt ? (behById.get(hitApt.behandler) || p.treat || "") : (p.treat || "") });
  });
  return importRecalls(rows, { onlyNewer: true });
}
